/**
 * TypeScript view of the `wordle` format (ADR 0054).
 * Source of truth: `shared/schema/wordle.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "en" | "fr";
export type Mark = "correct" | "present" | "absent";

export const LANGUAGES: Language[] = ["en", "fr"];
export const LENGTHS = [4, 5, 6, 7] as const;
export const DEFAULT_LENGTH = 5;

export interface WordleDefinition {
  puzzleType: "wordle";
  typeVersion: number;
  /** Picks the server's dictionary and the on-screen keyboard layout. */
  language: Language;
  /** Letters in the hidden word and in every guess, 4 to 7. */
  length: number;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** One marked guess: uppercase A–Z, one mark per letter. */
export interface Guess {
  word: string;
  marks: Mark[];
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface WordleProgress {
  version: 1;
  /** The letters being typed, uppercase A–Z. */
  draft: string;
  /** Every marked guess, in submission order. The found word has every letter correct. */
  guesses: Guess[];
  /** Words the dictionary refused, so the player is told without a transient state. */
  unknown?: string[];
}

export interface WordleSubmission {
  word: string;
}

export function lengthOf(definition: WordleDefinition): number {
  return definition.length ?? DEFAULT_LENGTH;
}
